// Note: all BaseItem children do not change object references
// but update only properties

export abstract class BaseItem {
    [key: string]: any

    public static readonly UNDEFINED_ID: number = -1; // see UNDEFINED_ID in Rest Api

    protected compareArray(a: Array<any>, b: Array<any>): boolean {
        if (a.length !== b.length) return false;
        for (let index = 0; index < a.length; index++) {
            if (a[index] instanceof BaseItem) {
                if (!a[index].isSame(b[index])) return false;
            } else if (a[index] instanceof Array) {
                if (!this.compareArray(a[index], b[index])) return false;
            } else if (a[index] !== b[index]) return false;
        }
        return true;
    }

    protected doIsSame(other: BaseItem, ignore: Array<string> = []): boolean {
        if (!other) return false;

        let myLength=0;
        Object.keys(this).forEach((key) => {if (!ignore.find((name) => {return (name == key) })) myLength+=1;})
        let otherLength=0;
        Object.keys(other).forEach((key) => {if (!ignore.find((name) => {return (name == key) })) otherLength+=1;})
        if (myLength !== otherLength) return false;

        for (const field of Object.keys(this)) {
            if (ignore.includes(field)) continue;
            if (!(field in other)) return false;
            if (this[field] instanceof Array) {
                if (!this.compareArray(this[field], other[field])) return false;
            } else if (this[field] instanceof BaseItem) {
                if (!this[field].isSame(other[field])) return false;
            } else if (this[field] !== other[field]) return false;
        }
        return true;
    }

    // javascript deep copy like json update
    protected doUpdateFromJson(values: { [index: string]: any; }, classArrays: Array<{ field: string, type: object }> = []) {
        if (!values) return;

        for (const field of Object.keys(values)) {
            if (field in this) {
                if (this[field] instanceof BaseItem) this[field].updateFromJson(values[field]);
                else if (this[field] instanceof Array) {
                    this[field].length = 0;
                    let arrayClass: object = {};
                    for (let a = 0; a < classArrays.length; a++) {
                        if (classArrays[a].field === field) arrayClass = classArrays[a].type;
                    }
                    for (let i = 0; i < values[field].length; i++) {
                        if (arrayClass instanceof BaseItem) {
                            const clone = <typeof arrayClass>new (arrayClass.constructor as { new(): typeof arrayClass })();
                            clone.updateFromJson(values[field][i]);
                            (<Array<unknown>>this[field as keyof this]).push(clone);
                        } else {
                            this[<any>field].push(Object.create(values[field][i]));
                        }

                    }
                } else this[field as keyof this] = values[field];
            }
        }
    }

    public abstract isSame(other: BaseItem): boolean;

    public abstract reset(): void;

    public abstract updateFromJson(values: BaseItem): void;

}

export class DeviceTypeItem extends BaseItem {
    id!: number;
    cat_0_id!: number;
    cat_1_id!: number;
    cat_2_id!: number;
    cat_3_id!: number;
    cat_0!: string;
    cat_1!: string;
    cat_2!: string;
    cat_3!: string;
    children!: number;

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        // numbers have to be zero to match plain json object returned from rest service
        // actual ids are starting with one
        this.id = BaseItem.UNDEFINED_ID;
        this.cat_0_id = BaseItem.UNDEFINED_ID;
        this.cat_1_id = BaseItem.UNDEFINED_ID;
        this.cat_2_id = BaseItem.UNDEFINED_ID;
        this.cat_3_id = BaseItem.UNDEFINED_ID;
        this.cat_0 = '';
        this.cat_1 = '';
        this.cat_2 = '';
        this.cat_3 = '';
        this.children = BaseItem.UNDEFINED_ID;
    }

    public updateFromJson(values: DeviceTypeItem): void {
        this.doUpdateFromJson(values);
    }


    public isSame(other: DeviceTypeItem): boolean {
        return this.doIsSame(other, ['cat_0', 'cat_1', 'cat_2', 'cat_3', 'children']);
    }
}

export class SmallSelectItem extends BaseItem {
    id!: number | string;
    name!: string;
    selected?: boolean;  // optional to read it from REST object

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.id = '';
        this.name = '';
        this.selected = false;
    }

    public updateFromJson(values: SmallSelectItem): void {
        this.doUpdateFromJson(values);
    }

    public isSame(other: SmallSelectItem): boolean {
        return this.doIsSame(other, ['name', 'selected']);
    }

}

export class StringSearch extends BaseItem {
    search!: string;
    exact!: boolean;

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.search = '';
        this.exact = false;
    }

    public isSame(other: StringSearch): boolean {
        return this.doIsSame(other);
    }

    public updateFromJson(values: StringSearch): void {
        this.doUpdateFromJson(values);
    }
}

export class SearchSort extends BaseItem {
    sortItems: SmallSelectItem[];
    direction!: string;

    constructor(private defaultSearchSort: SmallSelectItem) {
        super();
        this.sortItems = [];
        this.reset();
    }

    public reset(defaultSearchSort: SmallSelectItem = new SmallSelectItem()): void {
        this.sortItems.length = 0;
        this.defaultSearchSort.updateFromJson(defaultSearchSort);
        this.sortItems.push(this.defaultSearchSort);
        this.direction = '-';
    }

    isSame(other: SearchSort): boolean {
        return this.doIsSame(other);
    }

    public updateFromJson(values: SearchSort): void {
        this.doUpdateFromJson(values, [{field: 'sortItems', type: new SmallSelectItem()}]);
    }

}

export class DateRange extends BaseItem {
    from!: string;
    to!: string;

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.from = '';
        this.to = '';
    }

    public isSame(other: DateRange): boolean {
        return this.doIsSame(other);
    }

    public updateFromJson(values: DateRange): void {
        this.doUpdateFromJson(values);
    }
}

export class SearchData extends BaseItem {
    id!: number;
    simple!: boolean;
    onlyDepartment!: boolean;
    onlyUsable!: boolean;
    simpleSearch: StringSearch;
    simpleSort: SearchSort;
    freeSearch: StringSearch;
    category: DeviceTypeItem;
    department: SmallSelectItem[];
    deviceStatus: SmallSelectItem[];
    locationStatus: SmallSelectItem[];
    location: StringSearch;
    ownership: SmallSelectItem[];
    date: DateRange;
    sort: SearchSort;
    countResults!: number;

    constructor(private defaultSearchSort: SmallSelectItem) {
        super();
        this.simpleSearch = new StringSearch();
        this.simpleSort = new SearchSort(defaultSearchSort);
        this.freeSearch = new StringSearch();
        this.category = new DeviceTypeItem();
        this.department = [];
        this.deviceStatus = [];
        this.locationStatus = [];
        this.location = new StringSearch();
        this.ownership = [];
        this.date = new DateRange();
        this.sort = new SearchSort(defaultSearchSort);
        this.reset();
    }

    public reset(): void {
        this.id = BaseItem.UNDEFINED_ID;
        this.simple = true;
        this.onlyDepartment = true;
        this.onlyUsable = true;
        this.simpleSearch.reset();
        this.simpleSort.reset();
        this.freeSearch.reset();
        this.category.reset();
        this.department.length = 0;
        this.deviceStatus.length = 0;
        this.locationStatus = [];
        this.location.reset();
        this.ownership.length = 0;
        this.date.reset();
        this.sort.reset();
        this.countResults = 0;
    }

    public isSame(other: SearchData): boolean {
        return this.doIsSame(other, ['id', 'countResults']);
    }

    public updateFromJson(values: SearchData): void {
        this.doUpdateFromJson(values,
            [{field: 'department', type: new SmallSelectItem()},
                {field: 'deviceStatus', type: new SmallSelectItem()},
                {field: 'locationStatus', type: new SmallSelectItem()},
                {field: 'ownership', type: new SmallSelectItem()}]);
    }

}

export class UserSettings extends BaseItem {
    defaultPage!: string;
    defaultSearch: SearchData;

    constructor(private defaultSearchSort: SmallSelectItem) {
        super();
        this.defaultSearch = new SearchData(defaultSearchSort);
        this.reset();
    }

    public reset(): void {
        this.defaultPage = '';
        this.defaultSearch.reset();
    }

    public updateFromJson(values: UserSettings): void {
        this.doUpdateFromJson(values);
    }

    public isSame(other: UserSettings): boolean {
        return this.doIsSame(other);
    }
}

export class DeviceLocation extends BaseItem {
    [key: string]: any

    id!: string;
    full_name!: string;
    user_id!: number;
    last_edit!: string;
    cancelled!: boolean;
    location!: string;
    location_status!: string;
    location_status_id!: number;
    start_date!: string | null;
    end_date!: string | null;
    giver!: string;
    giver_email!: string;
    taker!: string;
    taker_email!: string;
    taker_address!: string;
    description!: string;

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.id = '';
        this.full_name = '';
        this.user_id = BaseItem.UNDEFINED_ID;
        this.last_edit = '';
        this.cancelled = false;
        this.location = '';
        this.location_status = '';
        this.location_status_id = BaseItem.UNDEFINED_ID;
        this.start_date = null;
        this.end_date = null;
        this.giver = '';
        this.giver_email = '';
        this.taker = '';
        this.taker_email = '';
        this.taker_address = '';
        this.description = '';
    }

    public isSame(other: DeviceLocation): boolean {
        return this.doIsSame(other);
    }

    public updateFromJson(values: DeviceLocation): void {
        this.doUpdateFromJson(values);
    }
}

export class DeviceLocationMarker {
    [key: string]: any

    id!: boolean;
    cancelled!: boolean;
    location!: boolean;
    location_status!: boolean;
    start_date!: boolean;
    end_date!: boolean;
    giver!: boolean;
    giver_email!: boolean;
    taker!: boolean;
    taker_email!: boolean;
    taker_address!: boolean;
    description!: boolean;

    constructor(defValue: boolean = false) {
        this.reset(defValue);
    }

    reset(defValue: boolean) {
        this.id = defValue;
        this.cancelled = defValue;
        this.location = defValue;
        this.location_status = defValue;
        this.start_date = defValue;
        this.end_date = defValue;
        this.giver = defValue;
        this.giver_email = defValue;
        this.taker = defValue;
        this.taker_email = defValue;
        this.taker_address = defValue;
        this.description = defValue;
    }
}

export class DeviceItem extends BaseItem {
    id!: string;
    full_name!: string;
    user_id!: number;
    last_edit!: string;
    invnr!: string;
    ean!: string;
    manufacturer!: string;
    model!: string;
    serial!: string;
    specification!: string;
    accessories!: string;
    url!: string;
    buy_date!: string | null;
    leasing_end!: string | null;
    device_status!: string;
    device_status_id!: number;
    ownership!: string;
    ownership_id!: number;
    price_netto!: number | null;
    price_brutto!: number | null;
    comment!: string;
    tuev_date!: string | null;
    type_id!: number;
    cat_0_id!: number;
    cat_1_id!: number;
    cat_2_id!: number;
    cat_3_id!: number;
    cat_0!: string;
    cat_1!: string;
    cat_2!: string;
    cat_3!: string;
    vendor!: string;
    owner!: string;
    department!: string;
    department_id!: number;
    sap_nr!: string;

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.id = '';
        this.full_name = '';
        this.user_id = BaseItem.UNDEFINED_ID;
        this.last_edit = '';
        this.invnr = '';
        this.ean = '';
        this.manufacturer = '';
        this.model = '';
        this.serial = '';
        this.specification = '';
        this.accessories = '';
        this.url = '';
        this.buy_date = null;
        this.leasing_end = '';
        this.device_status = '';
        this.device_status_id = BaseItem.UNDEFINED_ID;
        this.ownership = '';
        this.ownership_id = BaseItem.UNDEFINED_ID;
        this.price_netto = null;
        this.price_brutto = null;
        this.comment = '';
        this.tuev_date = null;
        this.type_id = BaseItem.UNDEFINED_ID;
        this.cat_0_id = BaseItem.UNDEFINED_ID;
        this.cat_1_id = BaseItem.UNDEFINED_ID;
        this.cat_2_id = BaseItem.UNDEFINED_ID;
        this.cat_3_id = BaseItem.UNDEFINED_ID;
        this.cat_0 = '';
        this.cat_1 = '';
        this.cat_2 = '';
        this.cat_3 = '';
        this.vendor = '';
        this.owner = '';
        this.department = '';
        this.department_id = BaseItem.UNDEFINED_ID;
        this.sap_nr = '';
    }

    public updateFromJson(values: DeviceItem): void {
        this.doUpdateFromJson(values);
    }

    public isSame(other: DeviceItem): boolean {
        return this.doIsSame(other);
    }

}

export class FullDevice extends BaseItem {
    device: DeviceItem;
    current_location: DeviceLocation;
    files: UploadedFile[];
    former_locations: DeviceLocation[];
    reservations: DeviceLocation[];
    position?: number;
    parentSystem?: string;

    constructor() {
        super();
        this.device = new DeviceItem();
        this.current_location = new DeviceLocation();
        this.files = [];
        this.former_locations = [];
        this.reservations = [];
        this.reset();
    }

    public reset(): void {
        this.device.reset();
        this.current_location.reset();
        this.files = [];
        this.former_locations.length = 0;
        this.reservations.length = 0;
        this.position = BaseItem.UNDEFINED_ID;
        this.parentSystem = '';
    }

    public isSame(other: FullDevice): boolean {
        return this.doIsSame(other, ['position']);
    }

    public updateFromJson(values: FullDevice): void {
        this.doUpdateFromJson(values,
            [{field: 'former_locations', type: new DeviceLocation()},
                {field: 'reservations', type: new DeviceLocation()}]);
    }
}


export class DeviceItemMarker {
    [key: string]: any

    id!: boolean;
    invnr!: boolean;
    ean!: boolean;
    manufacturer!: boolean;
    model!: boolean;
    serial!: boolean;
    specification!: boolean;
    accessories!: boolean;
    url!: boolean;
    buy_date!: boolean;
    leasing_end!: boolean;
    device_status!: boolean;
    ownership!: boolean;
    price_netto!: boolean;
    price_brutto!: boolean;
    comment!: boolean;
    tuev_date!: boolean;
    type_id!: boolean;
    vendor!: boolean;
    owner!: boolean;
    department!: boolean;
    sap_nr!: boolean;
    files!: boolean;
    parentSystem!: boolean;

    constructor(defValue: boolean = false) {
        this.reset(defValue);
    }

    reset(defValue: boolean) {
        this.id = defValue;
        this.invnr = defValue;
        this.ean = defValue;
        this.manufacturer = defValue;
        this.model = defValue;
        this.serial = defValue;
        this.specification = defValue;
        this.accessories = defValue;
        this.url = defValue;
        this.buy_date = defValue;
        this.leasing_end = defValue;
        this.device_status = defValue;
        this.ownership = defValue;
        this.price_netto = defValue;
        this.price_brutto = defValue;
        this.comment = defValue;
        this.tuev_date = defValue;
        this.type_id = defValue;
        this.vendor = defValue;
        this.owner = defValue;
        this.department = defValue;
        this.sap_nr = defValue;
        this.files = defValue;
        this.parentSystem = defValue;
    }
}

export class User extends BaseItem {
    [key: string]: any

    position?: number;
    id!: number
    active!: boolean;
    user_name!: string;
    full_name!: string;
    role_id_bits!: number;
    department_id!: number;
    department!: string;
    description!: string;
    search?: boolean;
    export?: boolean;
    relocate?: boolean;
    relocateForeign?: boolean;
    lend?: boolean;
    lendForeign?: boolean;
    edit?: boolean;
    editForeign?: boolean;
    label?: boolean;
    history?: boolean;
    admin?: boolean;
    departmentSelect?: SmallSelectItem[];

    constructor() {
        super();
        this.reset();
    }

    public reset(): void {
        this.position = BaseItem.UNDEFINED_ID;
        this.id = BaseItem.UNDEFINED_ID;
        this.active = false;
        this.user_name = '';
        this.full_name = '';
        this.role_id_bits = 0;
        this.department_id = BaseItem.UNDEFINED_ID;
        this.department = '';
        this.description = '';
        this.search = false;
        this.export = false;
        this.relocate = false;
        this.relocateForeign = false;
        this.lend = false;
        this.lendForeign = false;
        this.edit = false;
        this.editForeign = false;
        this.label = false;
        this.history = false;
        this.admin = false;
        this.departmentSelect = [];
    }

    public isSame(other: User): boolean {
        return this.doIsSame(other, ['position', 'departmentSelect']);
    }

    public updateFromJson(values: User): void {
        this.doUpdateFromJson(values, [{field: 'departmentSelect', type: new SmallSelectItem()}]);
    }
}

export class AuthData {
    loggedIn!: boolean;
    userName!: string;
    realName!: string;
    email!: string;
    role!: number;
    department!: string;

    constructor() {
        this.reset();
    }

    reset() {
        this.loggedIn = false;
        this.userName = '';
        this.realName = '';
        this.email = '';
        this.role = 0;
        this.department = '';
    }
}


export interface ServerError {
    error: string;
    message: string;
}

export interface ServerResult<T> {
    item: T;
}

export interface ServerResults<T> {
    items: Array<T>;
    total: number;
}

export type ServerAnswer =
    ServerResults<any>
    | ServerResult<any>
    | Blob
    | SystemTree
    | InitDeviceResponse
    | InitLocationResponse
    | DeviceLocation;
export type ServerAnswerWithError = ServerError | ServerAnswer;

export interface DeviceItemInList {

    position?: number;
    id: string;
    full_name: string;
    user_id: number;
    last_edit: string;
    invnr: string;
    ean: string;
    manufacturer: string;
    model: string;
    serial: string;
    specification: string;
    accessories: string;
    url: string;
    buy_date: string | null;
    leasing_end: string | null;
    device_status: string;
    device_status_id: number;
    ownership: string;
    ownership_id: number;
    price_netto: number | null;
    price_brutto: number | null;
    comment: string;
    tuev_date: string | null;
    type_id: number;
    cat_0_id: number;
    cat_1_id: number;
    cat_2_id: number;
    cat_3_id: number;
    cat_0: string;
    cat_1: string;
    cat_2: string;
    cat_3: string;
    vendor: string;
    owner: string;
    department: string;
    department_id: number;
    sap_nr: string;

    location: string;
    location_status: string;
    start_date: string | null;
    end_date: string | null;
    taker: string;
    taker_email: string;
    taker_address: string;
    description: string;
}

export interface UploadedFile {
    id: string;
    file: string;
    thumb: string;
    name: string;
    filetype: string;
}

export interface DeviceInSystemTree {
    [key: string]: any

    cat_0: string;
    cat_1: string;
    cat_2: string;
    cat_3: string;
    id: string;
    invnr: string;
    manufacturer: string;
    model: string;
    device_status: string;
    location: string;
    location_status: string;
}

export interface EdgeInSystemTree {
    p: string;
    c: string;
}

export interface SystemTree {
    [key: string]: any

    nodes: DeviceInSystemTree[];
    edges: EdgeInSystemTree[];
    isSystem: boolean;
    belongsToSystem: boolean;
    parent: DeviceInSystemTree | null;
}

export interface LogHistory {
    [key: string]: any

    // no NULL values reported from database, -1 on unknown ids
    id: string;
    operator_id: number;
    operator: string;
    desc: string;
    date: string;
    user_id: number;
    full_name: string;
    device_id: string;
    description: string;
    event: string;
}

export interface InitDeviceResponse {
    device_id: string;
    location_id: string;
}

export interface InitLocationResponse {
    location_id: string;
}

export interface DeviceLocationErrorText {
    [key: string]: any

    id?: string;
    cancelled?: string;
    location?: string;
    location_status?: string;
    start_date?: string;
    end_date?: string;
    giver?: string;
    giver_email?: string;
    taker?: string;
    taker_email?: string;
    taker_address?: string;
    description?: string;
}

export interface DeviceItemErrorText {
    [key: string]: any

    id?: string;
    invnr?: string;
    ean?: string;
    manufacturer?: string;
    model?: string;
    serial?: string;
    specification?: string;
    accessories?: string;
    url?: string;
    buy_date?: string;
    leasing_end?: string;
    device_status?: string;
    ownership?: string;
    price_netto?: string;
    price_brutto?: string;
    comment?: string;
    tuev_date?: string;
    type_id?: string;
    vendor?: string;
    owner?: string;
    department?: string;
    sap_nr?: string;
    current_location?: string;
    files?: string;
    parentSystem?: string;
}

export interface Department {
    [key: string]: any

    position?: number;
    id: number;
    name: string;
    selectable: boolean;
    writable: boolean;
}
